import { useState } from "react";
import { CheckCircle2, Loader2, Mail, X } from "lucide-react";
import { motion as Motion } from "framer-motion";
import { GetImageUrl } from "../../../helpers/GetImageUrl";

const ComplaintModal = ({
  complaintDetail,
  onClose,
  handleResolve,
  loading,
  mode,
  isResolving,
}) => {
  const [resolutionNote, setResolutionNote] = useState("");

  if (loading || !complaintDetail) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-xl">
        <Loader2 className="animate-spin text-white" size={32} />
      </div>
    );
  }

  const isResolveMode = mode === "resolve";

  return (
    <Motion.div
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="
          fixed
          top-1/2 left-1/2
          -translate-x-1/2 -translate-y-1/2
          w-[92%]

          max-h-[90vh]
          overflow-y-auto
          rounded-md
          bg-white
          shadow-2xl
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-8 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isResolveMode
                ? "Resolve Complaint & Send Email"
                : "Complaint Details"}
            </h2>
            <p className="text-sm text-gray-600">
              {isResolveMode
                ? "Review the complaint before confirming resolution"
                : "View customer complaint information"}
            </p>
          </div>

          <button
            onClick={onClose}
            className="h-10 w-10 rounded-lg bg-gray-100 hover:bg-red-500 hover:text-white transition flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        {isResolveMode ? (
          <div className="px-4 py-6 sm:px-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Customer Name" value={complaintDetail.name} />
              <Field label="Email" value={complaintDetail.email} />
              <Field label="Service" value={complaintDetail.service} />
              <Field
                label="Complaint Type"
                value={complaintDetail.complaintType}
              />
              <div className="sm:col-span-2">
                <Field
                  label="Complaint Description"
                  value={complaintDetail.description}
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="resolution-note"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Resolution Note <span className="text-gray-400">(optional)</span>
                </label>
                <textarea
                  id="resolution-note"
                  value={resolutionNote}
                  onChange={(event) => setResolutionNote(event.target.value)}
                  maxLength={2000}
                  rows={5}
                  disabled={isResolving}
                  placeholder="Briefly describe how the issue was resolved."
                  className="w-full resize-y rounded-lg border border-gray-300 px-4 py-3 text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100"
                />
                <p className="mt-1 text-right text-xs text-gray-500">
                  {resolutionNote.length}/2000
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={isResolving}
                className="rounded-lg border border-gray-300 px-5 py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() =>
                  handleResolve(complaintDetail._id, resolutionNote)
                }
                disabled={isResolving}
                className="flex items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isResolving ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Mail size={18} />
                )}
                {isResolving
                  ? "Sending..."
                  : "Confirm Resolution & Send Email"}
              </button>
            </div>
          </div>
        ) : (
          <>

        {complaintDetail.status === "resolved" ? (
          complaintDetail.mediaDeletedAt ? (
            <div className="mt-4 text-center rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-800">
              Complaint resolved. Attachments have been permanently deleted in
              accordance with our data retention policy.
            </div>
          ) : (
            <div className="mt-4 text-center rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-800">
              This complaint has been resolved. Attachments will be permanently
              deleted 2 days after resolution in accordance with our data
              retention policy.
            </div>
          )
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-4 py-8">
          <Field label="Customer Name" value={complaintDetail?.name} />
          <Field label={"Phone"} value={complaintDetail?.phone} />
          <Field label="Email" value={complaintDetail.email} />
          <Field label="Address" value={complaintDetail.address} />
          <Field label="Service" value={complaintDetail.service} />
          <Field label="Model" value={complaintDetail.model} />
          <Field label="Serial Number" value={complaintDetail.serialNumber} />
          <Field label="Complaint Type" value={complaintDetail.complaintType} />

          <Field label={"Brand"} value={complaintDetail?.brand?.brandName} />
          <Field label={"Support"} value={complaintDetail?.support} />
          <Field
            label={"Installation"}
            value={
              complaintDetail?.installedByUs
                ? "Installed by Us"
                : "Outside Installation"
            }
          />

          <Field
            label="Installation Date"
            value={
              complaintDetail.installationDate
                ? new Date(
                    complaintDetail.installationDate,
                  ).toLocaleDateString()
                : "-"
            }
          />
          {complaintDetail.createdAt && (
            <Field
              label="Complaint Created At"
              value={new Date(complaintDetail.createdAt).toLocaleString()}
            />
          )}
          <div className="w-full">
            <Field label="Status" value={complaintDetail?.status} />
          </div>

          {complaintDetail.resolutionEmailSent && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 size={18} />
                Resolution email sent
              </div>
              {complaintDetail.resolutionEmailSentAt && (
                <p className="mt-1 text-sm">
                  {new Date(
                    complaintDetail.resolutionEmailSentAt,
                  ).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {complaintDetail.resolvedAt && (
            <Field
              label="Resolved At"
              value={new Date(complaintDetail.resolvedAt).toLocaleString()}
            />
          )}

          {complaintDetail.resolutionNote && (
            <div className="md:col-span-2">
              <Field
                label="Resolution Note"
                value={complaintDetail.resolutionNote}
              />
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block mb-2 text-sm font-medium text-gray-600">
              Complaint
            </label>
            <div className="min-h-32 rounded-lg border border-gray-200 bg-gray-50 p-4 whitespace-pre-wrap break-words text-gray-800">
              {complaintDetail.description || "No description"}
            </div>
          </div>
        </div>

        {complaintDetail?.photos?.length > 0 && (
          <div className="px-4 pb-8">
            <label className="mb-3 block text-sm font-medium text-gray-600">
              Photos
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {complaintDetail?.photos?.map((photo, index) => {
                const imageUrl = GetImageUrl(photo);

                return (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`Complaint ${index + 1}`}
                    className="w-full h-48 sm:h-56 lg:h-64 rounded-lg border border-gray-200 bg-gray-100 object-contain"
                  />
                );
              })}
            </div>
          </div>
        )}

        {complaintDetail?.videos?.length > 0 && (
          <div className="px-4 pb-8">
            <label className="mb-3 block text-sm font-medium text-gray-600">
              Videos
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {complaintDetail?.videos?.length > 0 ? (
                complaintDetail.videos.map((video, index) => {
                  const videoUrl = GetImageUrl(video);

                  return (
                    <video
                      key={index}
                      controls
                      preload="metadata"
                      className="w-full rounded-lg border border-gray-200"
                    >
                      <source src={videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500">No videos uploaded.</p>
              )}
            </div>
          </div>
        )}
          </>
        )}
      </Motion.div>
    </Motion.div>
  );
};

function Field({ label, value }) {
  return (
    <div className="w-full">
      <label className="mb-2 block text-sm font-medium text-gray-600">
        {label}
      </label>

      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 sm:px-4 text-gray-900 break-words">
        {value ?? "-"}
      </div>
    </div>
  );
}

export default ComplaintModal;
